import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Layout from "@/components/Layout";
import CreateTopicDialog from "@/components/CreateTopicDialog";
import { useCommunityCategories } from "@/hooks/useCommunityCategories";
import {
  MessageSquare,
  Clock,
  Eye,
  Star,
  Edit,
  Trash2,
  Search,
  Plus,
  Pin,
  Lock
} from "lucide-react";
import { communityApi } from "@/api";
import { useToast } from '@/hooks/use-toast';

interface Topic {
  id?: string | number;
  title: string;
  content?: string;
  tags?: string;
  topicTypeId?: number | string;
  isPinned?: boolean;
  isLocked?: boolean;
  createTime?: string;
  commentCount?: number;
  readCount?: number;
  collectCount?: number;
}

const MyTopics = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [filteredTopics, setFilteredTopics] = useState<Topic[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("-1");
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTopicId, setDeletingTopicId] = useState<string | number | null>(null);
  const { categories } = useCommunityCategories();
  const { toast } = useToast();

  const getCategoryInfo = (categoryId: string | number) => {
    return categories.find(cat => Number(cat.dictValue) === Number(categoryId)) || categories[0];
  };

  // 获取我的话题列表
  const fetchMyTopics = async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!token) {
      navigate('/');
      return;
    }

    setLoading(true);
    try {
      const params: any = {};
      if (searchTerm) params.title = searchTerm;
      if (selectedCategory !== "-1") params.topicTypeId = selectedCategory;

      const response = await communityApi.getMyTopics(params);
      setTopics(response.rows || []);
      setFilteredTopics(response.rows || []);
    } catch (error) {
      console.error("获取话题列表失败:", error);
      toast({
        title: "加载失败",
        description: "获取话题列表失败，请稍后重试",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTopics();
  }, []);

  // 搜索和筛选
  useEffect(() => {
    let filtered = topics;

    if (searchTerm) {
      filtered = filtered.filter(topic =>
        topic.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== "-1") {
      filtered = filtered.filter(topic =>
        String(topic.topicTypeId) === selectedCategory
      );
    }

    setFilteredTopics(filtered);
  }, [searchTerm, selectedCategory, topics]);

  // 处理编辑
  const handleEdit = (topic: Topic) => {
    setEditingTopic(topic);
    setIsEditDialogOpen(true);
  };

  // 处理删除
  const handleDeleteClick = (topicId: string | number) => {
    setDeletingTopicId(topicId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTopicId) return;

    try {
      const response = await communityApi.deleteTopic(deletingTopicId);
      if (response?.code === 200) {
        toast({
          title: "删除成功",
          description: "话题已删除",
        });
        fetchMyTopics();
      } else {
        toast({
          title: "删除失败",
          description: response?.msg || "删除话题失败，请稍后重试",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("删除话题失败:", error);
      toast({
        title: "删除失败",
        description: "删除话题失败，请稍后重试",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setDeletingTopicId(null);
    }
  };

  // 处理查看详情
  const handleView = (topicId: string | number) => {
    navigate(`/community/topic/${topicId}`);
  };

  return (
    <Layout>
      {/* Header */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              我的讨论
            </h1>
            <p className="text-xl text-gray-600">
              管理您发布的所有讨论内容
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
              {/* 搜索框 */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  placeholder="搜索我的讨论..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* 分类筛选 */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="选择分类" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <SelectItem key={category.dictValue} value={category.dictValue}>
                        <div className="flex items-center space-x-2">
                          <IconComponent className="w-4 h-4" />
                          <span>{category.dictLabel}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* 发起讨论按钮 */}
            <Button onClick={() => setIsCreateDialogOpen(true)} className="w-full md:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              发起讨论
            </Button>
          </div>
        </div>
      </section>

      {/* Topics List */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-pulse">加载中...</div>
            </div>
          ) : filteredTopics.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || selectedCategory !== "-1" ? "未找到相关讨论" : "还没有发布讨论"}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm || selectedCategory !== "-1" 
                  ? "尝试调整搜索条件或筛选项" 
                  : "发起您的第一个讨论，与社区成员分享您的想法"}
              </p>
              {!searchTerm && selectedCategory === "-1" && (
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  发起讨论
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTopics.map((topic) => {
                const categoryInfo = getCategoryInfo(topic.topicTypeId);
                return (
                  <Card key={topic.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 cursor-pointer" onClick={() => handleView(topic.id)}>
                          <div className="flex items-center space-x-2 mb-3">
                            {topic.isPinned && (
                              <Pin className="h-4 w-4 text-red-500" />
                            )}
                            {topic.isLocked && (
                              <Lock className="h-4 w-4 text-gray-500" />
                            )}
                            <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600">
                              {topic.title}
                            </h3>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 mb-3">
                            <Badge className={categoryInfo.color}>
                              {(() => {
                                const IconComponent = categoryInfo.icon;
                                return <IconComponent className="w-3 h-3" />;
                              })()}
                              <span className="ml-1">{categoryInfo.dictLabel}</span>
                            </Badge>
                            {topic?.tags && topic?.tags?.replace(/，/g, ',').split(',').map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {tag.trim()}
                              </Badge>
                            ))}
                          </div>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Clock className="h-4 w-4" />
                              <span>{topic.createTime}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Eye className="h-4 w-4" />
                              <span>{topic.readCount || 0} 浏览</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <MessageSquare className="h-4 w-4" />
                              <span>{topic.commentCount || 0} 回复</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Star className="h-4 w-4" />
                              <span>{topic.collectCount || 0} 收藏</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex space-x-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(topic)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            编辑
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(topic.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            删除
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* 创建讨论对话框 */}
      <CreateTopicDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={fetchMyTopics}
      />

      {/* 编辑讨论对话框 */}
      {editingTopic && (
        <CreateTopicDialog
          isOpen={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setEditingTopic(null);
          }}
          onSuccess={fetchMyTopics}
          isEditMode={true}
          topicData={{
            id: editingTopic.id,
            title: editingTopic.title,
            topicTypeId: editingTopic.topicTypeId,
            content: editingTopic.content,
            tags: editingTopic.tags
          }}
        />
      )}

      {/* 删除确认对话框 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              您确定要删除这个讨论吗？此操作无法撤销，删除后所有相关的评论和数据都将被永久删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              确认删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default MyTopics;
